import useGetMe from '@/api/queries/me/useGetMe';
import useAgreeToTermsAndPrivacyVersion from '@/api/rest/termsAndPrivacy/useAgreeToTermsAndPrivacyVersion';
import useGetTermsAndPrivacyUpdates from '@/api/rest/termsAndPrivacy/useGetTermsAndPrivacyUpdates';
import {
  clearLocalStorage,
  clearLocalStorageIfAccountChanged,
} from '@/helpers/localStorageKeyHelper';
import {
  AccountAccessBanner,
  MaintenanceBanner,
  OfflineBanner,
} from '@/ui/components';
import ChatAgent from '@/ui/components/chatAgent/ChatAgent';
import TermsAndPrivacyModal from '@/ui/layouts/TermsAndPrivacyModal';
import SessionStatusBanner from '@/ui/layouts/components/SessionStatusBanner';
import useDevelopmentTools from '@/ui/layouts/components/useDevelopmentTools';
import moment from 'moment-timezone';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type ChatAgentContextType = {
  hasChatAgentAccess: boolean;
  show: boolean;
  showChatAgent: () => void;
  hideChatAgent: () => void;
};

const ChatAgentContext = createContext<ChatAgentContextType>({
  hasChatAgentAccess: false,
  show: false,
  showChatAgent: () => {},
  hideChatAgent: () => {},
});

const AuthenticatedRootLayout: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  useDevelopmentTools();

  const { hasChatAgentAccess, user } = useGetMe();

  // Detect account switches (e.g. via another tab or the admin app). On first load we
  // record the baseline; if the active account later changes while this tab is open we
  // reset all client state the same way the initiating tab does.
  const loadedAccountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const accountId = user?.account.id;
    if (!accountId) return;

    if (loadedAccountIdRef.current === undefined) {
      loadedAccountIdRef.current = accountId;
      clearLocalStorageIfAccountChanged(accountId);
      return;
    }

    if (loadedAccountIdRef.current !== accountId) {
      clearLocalStorage();
      window.location.reload();
    }
  }, [user?.account.id]);

  const [isShown, setIsShown] = useState(false);
  const showChatAgent = useCallback(() => setIsShown(true), []);
  const hideChatAgent = useCallback(() => setIsShown(false), []);

  useEffect(() => {
    moment.updateLocale('en', { week: { dow: 1 } });
  }, []);

  // Terms & Privacy
  const { data: termsUpdate } = useGetTermsAndPrivacyUpdates();
  const agreeMutation = useAgreeToTermsAndPrivacyVersion();

  useEffect(() => {
    if (!hasChatAgentAccess) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsShown((prev) => !prev);
      }

      if (e.key === 'Escape' && isShown) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setIsShown(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [hasChatAgentAccess, isShown]);

  const contextValue = useMemo(
    () => ({
      hasChatAgentAccess,
      show: isShown,
      showChatAgent,
      hideChatAgent,
    }),
    [hasChatAgentAccess, isShown, showChatAgent, hideChatAgent]
  );

  return (
    <ChatAgentContext.Provider value={contextValue}>
      <div
        className="grid h-svh overflow-hidden bg-gray-a7"
        style={{
          gridTemplateRows: 'auto 1fr',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gridTemplateAreas: `
            "banner  banner"
            "content sidebar"
          `,
        }}
      >
        <div style={{ gridArea: 'banner' }}>
          <OfflineBanner />
          <MaintenanceBanner />
          <SessionStatusBanner />
          <AccountAccessBanner />
          <div id="banner-portal" />
        </div>
        <div
          className="min-w-0 min-h-0 overflow-hidden"
          style={{ gridArea: 'content' }}
        >
          {children}
        </div>
        {hasChatAgentAccess && (
          <div
            className="min-w-0 min-h-0 overflow-hidden"
            style={{ gridArea: 'sidebar' }}
          >
            <ChatAgent isOpen={isShown} />
          </div>
        )}
      </div>

      <TermsAndPrivacyModal
        isOpen={!!termsUpdate}
        termsMarkdown={termsUpdate?.termsMarkdown}
        privacyMarkdown={termsUpdate?.privacyMarkdown}
        onAgree={() => {
          if (termsUpdate?.version) {
            agreeMutation.mutate(termsUpdate.version);
          }
        }}
        isAgreeing={agreeMutation.isPending}
      />
    </ChatAgentContext.Provider>
  );
};

export const useChatAgent = () => useContext(ChatAgentContext);

export default AuthenticatedRootLayout;
